import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiList, FiChevronLeft, FiChevronRight, FiCheck, FiBriefcase, FiUsers, FiClipboard, FiFileText, FiArrowLeft, FiUpload, FiLoader } from 'react-icons/fi';
import Modal from '../../components/Modal';
import DataTable from '../../components/DataTable';
import { questionnairesApi, assessmentsApi } from '../../services/api';

const statusOptions = [
  { value: 1, label: 'Active' },
  { value: 2, label: 'Inactive' },
  { value: 3, label: 'Archived' },
  { value: 4, label: 'Draft' }
];

const getStatusLabel = (status) => {
  const option = statusOptions.find(o => o.value === status);
  return option ? option.label : 'Unknown';
};

const getStatusColor = (status) => {
  switch (status) {
    case 1: return 'bg-[#0077b5]/20 text-[#0077b5]';
    case 2: return 'bg-[#919191]/20 text-[#919191]';
    case 3: return 'bg-[#F47F2E]/20 text-[#F47F2E]';
    case 4: return 'bg-[#53545a]/20 text-[#53545a]';
    default: return 'bg-[#919191]/20 text-[#919191]';
  }
};

const initialFormState = {
  categoryId: '',
  industry: '',
  questionnaireName: '',
  author: '',
  versionId: '',
  library: '',
  level: '',
  archivalStatus: 0,
  subgroup: '',
  status: 1,
  questionnaireDesc: '',
  derived: false,
  focusAreaId: 0,
  keywords: '',
  standards: '',
  departmentId: 0,
  disciplineId: 0
};

const Questionnaires = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentId = searchParams.get('assessmentId');
  const [assessmentContext, setAssessmentContext] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importParsedRows, setImportParsedRows] = useState([]);
  const [importSelectedRows, setImportSelectedRows] = useState([]);
  const [importSaveResult, setImportSaveResult] = useState(null);
  const [importStep, setImportStep] = useState('upload');
  const importFileRef = useRef(null);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [isEditing, setIsEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Toast notification
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Lookups
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [industries, setIndustries] = useState([]);
  

  const [validationErrors, setValidationErrors] = useState({});

  const steps = [
    { id: 0, title: 'Account', description: 'Organization info', icon: FiBriefcase },
    { id: 1, title: 'User', description: 'Author & ownership', icon: FiUsers },
    { id: 2, title: 'Assessment', description: 'Details & level', icon: FiClipboard },
    { id: 3, title: 'Questionnaires', description: 'Standards & tags', icon: FiFileText }
  ];

  // Fetch questionnaires
  const fetchQuestionnaires = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await questionnairesApi.getAll(1, 10000, null, null, assessmentId ? parseInt(assessmentId) : null);
      setQuestionnaires(response.data || []);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch questionnaires:', err);
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  // Fetch lookups
  const fetchLookups = useCallback(async () => {
    try {
      const [categoriesRes, departmentsRes, focusAreasRes, disciplinesRes, industriesRes] = await Promise.all([
        assessmentsApi.getCategories(),
        assessmentsApi.getDepartments(),
        assessmentsApi.getFocusAreas(),
        assessmentsApi.getDisciplines(),
        assessmentsApi.getIndustries()
      ]);
      console.log('Lookups fetched:', { categoriesRes, departmentsRes, focusAreasRes, disciplinesRes, industriesRes });
      setCategories(categoriesRes || []);
      setDepartments(departmentsRes || []);
      setFocusAreas(focusAreasRes || []);
      setDisciplines(disciplinesRes || []);
      setIndustries(industriesRes || []);

    } catch (err) {
      console.error('Failed to fetch lookups:', err);
    }
  }, []);

  useEffect(() => {
    fetchQuestionnaires();
    fetchLookups();
  }, [fetchQuestionnaires, fetchLookups]);

  // Fetch assessment details if assessmentId is present
  useEffect(() => {
    if (assessmentId) {
      assessmentsApi.getById(assessmentId)
        .then(data => setAssessmentContext(data))
        .catch(err => console.error('Failed to fetch assessment context:', err));
    } else {
      setAssessmentContext(null);
    }
  }, [assessmentId]);

  // Navigation to Questions page
  const handleViewQuestions = (questionnaire) => {
    const params = new URLSearchParams({
      questionnaireId: questionnaire.questionnaireId,
      name: questionnaire.questionnaireName
    });
    if (assessmentId) {
      params.append('assessmentId', assessmentId);
    }
    navigate(`/questions?${params.toString()}`);
  };

  // Modal handlers
  const openCreateModal = () => {
    setFormData(initialFormState);
    setIsEditing(false);
    setCurrentStep(0);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openEditModal = (questionnaire) => {
    setFormData({
      categoryId: questionnaire.categoryId ? String(questionnaire.categoryId) : '',
      industry: questionnaire.industry ? String(questionnaire.industry) : '',
      questionnaireName: questionnaire.questionnaireName ? String(questionnaire.questionnaireName) : '',
      author: questionnaire.author ? String(questionnaire.author) : '',
      versionId: questionnaire.versionId ? String(questionnaire.versionId) : '',
      library: questionnaire.library ? String(questionnaire.library) : '',
      level: questionnaire.level ? String(questionnaire.level) : '',
      archivalStatus: questionnaire.archivalStatus || 0,
      subgroup: questionnaire.subgroup ? String(questionnaire.subgroup) : '',
      status: questionnaire.status || 1,
      questionnaireDesc: questionnaire.questionnaireDesc ? String(questionnaire.questionnaireDesc) : '',
      derived: questionnaire.derived || false,
      focusAreaId: questionnaire.focusAreaId ? String(questionnaire.focusAreaId) : '',
      keywords: questionnaire.keywords ? String(questionnaire.keywords) : '',
      standards: questionnaire.standards ? String(questionnaire.standards) : '',
      departmentId: questionnaire.departmentId ? String(questionnaire.departmentId) : '',
      disciplineId: questionnaire.disciplineId ? String(questionnaire.disciplineId) : ''
    });
    setSelectedQuestionnaire(questionnaire);
    setIsEditing(true);
    setCurrentStep(0);
    setValidationErrors({});
    setIsModalOpen(true);
  };

  const openDeleteModal = (questionnaire) => {
    setSelectedQuestionnaire(questionnaire);
    setIsDeleteModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    switch (step) {
      case 0: // Account
        if (!formData.categoryId) errors.categoryId = 'Category is required';
        if (!formData.departmentId) errors.departmentId = 'Department is required';
        break;
      case 1: // User
        if (!formData.author?.trim()) errors.author = 'Author is required';
        break;
      case 2: // Assessment
        if (!formData.questionnaireName?.trim()) errors.questionnaireName = 'Questionnaire name is required';
        if (!formData.status) errors.status = 'Status is required';
        break;
      case 3: // Questionnaires
        break;
      default:
        break;
    }
    return errors;
  };

  const handleNext = () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setValidationErrors({});
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    const errors = validateStep(currentStep);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    try {
      const payload = {
        ...formData,
        categoryId: formData.categoryId ? parseInt(formData.categoryId) : null,
        versionId: formData.versionId ? parseInt(formData.versionId) : null,
        focusAreaId: formData.focusAreaId ? parseInt(formData.focusAreaId) : null,
        departmentId: formData.departmentId ? parseInt(formData.departmentId) : null,
        disciplineId: formData.disciplineId ? parseInt(formData.disciplineId) : null,
        status: parseInt(formData.status),
        
      };

      if (isEditing) {
        await questionnairesApi.update(selectedQuestionnaire.questionnaireId, payload);
      } else {
        await questionnairesApi.create(payload, assessmentId);
      }
      setIsModalOpen(false);
      fetchQuestionnaires();
    } catch (err) {
      console.error('Failed to save questionnaire:', err);
      alert('Failed to save questionnaire: ' + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      await questionnairesApi.delete(selectedQuestionnaire.questionnaireId);
      setIsDeleteModalOpen(false);
      fetchQuestionnaires();
    } catch (err) {
      console.error('Failed to delete questionnaire:', err);
      alert('Failed to delete questionnaire: ' + err.message);
    }
  };

  // Header-level import: uses the selected row(s)
  const handleHeaderImport = () => {
    if (selectedRowIds.length === 0) {
      showToast('Please select a questionnaire first.', 'error');
      return;
    }
    if (selectedRowIds.length > 1) {
      showToast('Please select only one questionnaire to import into.', 'error');
      return;
    }
    const q = questionnaires.find(q => q.questionnaireId === selectedRowIds[0]);
    if (q) openImportModal(q);
  };

  // Import functions (two-step: upload PDF → select questions → save)
  const openImportModal = (questionnaire = null) => {
    setImportFile(null);
    setImportParsedRows([]);
    setImportSelectedRows([]);
    setImportSaveResult(null);
    setImportLoading(false);
    setImportStep('upload');
    setSelectedQuestionnaire(questionnaire);
    setIsImportModalOpen(true);
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setImportFile(file);
    setImportParsedRows([]);
    setImportSelectedRows([]);
    setImportSaveResult(null);
    setImportStep('upload');
  };

  // Step 1: Parse the PDF → show extracted questions
  const handleImportParse = async () => {
    if (!importFile) {
      showToast('Please select a PDF file to import.', 'error');
      return;
    }

    setImportLoading(true);
    try {
      const questions = await questionnairesApi.importParse(importFile);
      if (!questions || questions.length === 0) {
        showToast('No questions found in the file.', 'error');
        setImportLoading(false);
        return;
      }
      setImportParsedRows(questions);
      // Pre-select all questions
      setImportSelectedRows(questions.map((_, idx) => idx));
      setImportStep('preview');
    } catch (err) {
      console.error('Parse failed:', err);
      showToast('Failed to parse file: ' + err.message, 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Toggle question selection
  const toggleImportRow = (idx) => {
    setImportSelectedRows(prev =>
      prev.includes(idx) ? prev.filter(r => r !== idx) : [...prev, idx]
    );
  };

  // Toggle select all / deselect all
  const toggleImportSelectAll = () => {
    if (importSelectedRows.length === importParsedRows.length) {
      setImportSelectedRows([]);
    } else {
      setImportSelectedRows(importParsedRows.map((_, idx) => idx));
    }
  };

  // Step 2: Save selected questions to the questionnaire
  const handleImportSave = async () => {
    const selectedQuestions = importParsedRows.filter((_, idx) => importSelectedRows.includes(idx));
    if (selectedQuestions.length === 0) {
      showToast('Please select at least one question to import.', 'error');
      return;
    }

    if (!selectedQuestionnaire) {
      showToast('No questionnaire selected for import.', 'error');
      return;
    }

    setImportLoading(true);
    try {
      const result = await questionnairesApi.importSave(
        selectedQuestionnaire.questionnaireId,
        selectedQuestions,
        assessmentId ? parseInt(assessmentId) : null
      );
      setImportSaveResult(result);
      setImportStep('result');

      if (result.successCount > 0) {
        fetchQuestionnaires();
      }

      if (result.failedCount === 0) {
        showToast(`Successfully imported ${result.successCount} question(s)!`, 'success');
      } else {
        showToast(
          `Imported ${result.successCount} of ${selectedQuestions.length} question(s). ${result.failedCount} failed.`,
          result.successCount > 0 ? 'warning' : 'error'
        );
      }
    } catch (err) {
      console.error('Import save failed:', err);
      showToast('Import failed: ' + err.message, 'error');
    } finally {
      setImportLoading(false);
    }
  };

  // Validation helpers
  const getInputClass = (fieldName) => `w-full px-4 py-2.5 border  text-sm focus:outline-none focus:ring-2 ${validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'} bg-white text-[#53545a]`;
  const getSelectClass = (fieldName) => `w-full px-4 py-2.5 border  text-sm focus:outline-none focus:ring-2 ${validationErrors[fieldName] ? 'border-[#F47F2E] focus:ring-[#F47F2E]' : 'border-[#919191]/30 focus:ring-[#0077b5]'} bg-white text-[#53545a]`;
  const renderFieldError = (fieldName) => validationErrors[fieldName] ? <p className="text-[#F47F2E] text-xs mt-1">{validationErrors[fieldName]}</p> : null;

  // Step content rendering
  const renderStepContent = () => {
    const labelClass = `block text-sm font-medium mb-2 text-[#53545a]`;

    switch (currentStep) {
      case 0: // Account
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Industry</label>
              <select name="industry" value={formData.industry} onChange={handleInputChange} className={getSelectClass('industryId')}>
                <option value="">Select Industry</option>
                {industries.map(i => (
                  <option key={i.industry} value={i.industry}>{i.industryName}</option>
                ))}
              </select>
              {renderFieldError('industry')}
            </div>
            <div>
              <label className={labelClass}>Category <span className="text-[#F47F2E]">*</span></label>
              <select name="categoryId" value={String(formData.categoryId)} onChange={handleInputChange} className={getSelectClass('categoryId')}>
                <option value="">Select Category</option>
                {categories.map(c => (
                  <option key={c.assessmentCategoryId} value={c.assessmentCategoryId}>{c.name}</option>
                ))}
              </select>
              {renderFieldError('categoryId')}
            </div>
            <div>
              {
                console.log("departmentId", formData.departmentId)
              }
              <label className={labelClass}>Department <span className="text-[#F47F2E]">*</span></label>
              <select name="departmentId" value={String(formData.departmentId)} onChange={handleInputChange} className={getSelectClass('departmentId')}>
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d.departmentId} value={d.departmentId}>{d.departmentDesc}</option>
                ))}
              </select>
              {renderFieldError('departmentId')}
            </div>
            <div>
              <label className={labelClass}>Subgroup</label>
              <input type="text" name="subgroup" value={formData.subgroup} onChange={handleInputChange} className={getInputClass('subgroup')} placeholder="e.g., Protect, Detect" />
              {renderFieldError('subgroup')}
            </div>
          </div>
        );

      case 1: // User
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelClass}>Author <span className="text-[#F47F2E]">*</span></label>
              <input type="text" name="author" value={formData.author} onChange={handleInputChange} className={getInputClass('author')} placeholder="e.g., Security Team" />
              {renderFieldError('author')}
            </div>
            <div>
              <label className={labelClass}>Focus Area</label>
              <select name="focusAreaId" value={formData.focusAreaId} onChange={handleInputChange} className={getSelectClass('focusAreaId')}>
                <option value="">Select Focus Area</option>
                {focusAreas.map(f => (
                  <option key={f.focusAreaId} value={f.focusAreaId}>{f.focusAreaDesc}</option>
                ))}
              </select>
              {renderFieldError('focusAreaId')}
            </div>
            <div>
              <label className={labelClass}>Discipline</label>
              <select name="disciplineId" value={formData.disciplineId} onChange={handleInputChange} className={getSelectClass('disciplineId')}>
                <option value="">Select Discipline</option>
                {disciplines.map(d => (
                  <option key={d.disciplineId} value={d.disciplineId}>{d.disciplineDesc}</option>
                ))}
              </select>
              {renderFieldError('disciplineId')}
            </div>
          </div>
        );

      case 2: // Assessment
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={labelClass}>Questionnaire Name <span className="text-[#F47F2E]">*</span></label>
              <input type="text" name="questionnaireName" value={formData.questionnaireName} onChange={handleInputChange} className={getInputClass('questionnaireName')} placeholder="e.g., NIST 800-53 Assessment" />
              {renderFieldError('questionnaireName')}
            </div>
            <div>
              <label className={labelClass}>Library</label>
              <select name="library" value={formData.library} onChange={handleInputChange} className={getSelectClass('library')}>
                <option value="">Select Library</option>
                <option value="NIST">NIST</option>
                <option value="ISO">ISO</option>
                <option value="COBIT">COBIT</option>
                <option value="NIST CSF">NIST CSF</option>
                <option value="SOC2">SOC2</option>
                <option value="HIPAA">HIPAA</option>
                <option value="PCI-DSS">PCI-DSS</option>
                <option value="Custom">Custom</option>
              </select>
              {renderFieldError('library')}
            </div>
            <div>
              <label className={labelClass}>Level</label>
              <select name="level" value={formData.level} onChange={handleInputChange} className={getSelectClass('level')}>
                <option value="">Select Level</option>
                <option value="Basic">Basic</option>
                <option value="Standard">Standard</option>
                <option value="Advanced">Advanced</option>
                <option value="Expert">Expert</option>
              </select>
              {renderFieldError('level')}
            </div>
            <div>
              <label className={labelClass}>Status <span className="text-[#F47F2E]">*</span></label>
              <select name="status" value={formData.status} onChange={handleInputChange} className={getSelectClass('status')}>
                {statusOptions.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {renderFieldError('status')}
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea name="questionnaireDesc" value={formData.questionnaireDesc} onChange={handleInputChange} className={getInputClass('questionnaireDesc')} rows={3} placeholder="Brief description of the questionnaire" />
              {renderFieldError('questionnaireDesc')}
            </div>
          </div>
        );

      case 3: // Questionnaires
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <label className={labelClass}>Standards</label>
              <input type="text" name="standards" value={formData.standards} onChange={handleInputChange} className={getInputClass('standards')} placeholder="e.g., AC-2, A.9.2, PR.DS-1" />
              {renderFieldError('standards')}
            </div>
            <div className="md:col-span-2">
              <label className={labelClass}>Keywords (comma-separated)</label>
              <input type="text" name="keywords" value={formData.keywords} onChange={handleInputChange} className={getInputClass('keywords')} placeholder="e.g., access control, security, compliance" />
              {renderFieldError('keywords')}
            </div>
            <div className="md:col-span-2 flex items-center gap-2 mt-2">
              <input type="checkbox" name="derived" checked={formData.derived} onChange={handleInputChange} className="w-4 h-4 text-[#0077b5] rounded focus:ring-[#0077b5]" />
              <label className={`text-sm font-medium text-[#53545a]`}>Derived Questionnaire</label>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // DataTable column definitions
  const tableColumns = [
    {
      key: 'questionnaireName',
      header: 'Questionnaire',
      sortable: true,
      filterable: true,
      render: (q) => (
        <div>
          <p className="text-sm font-medium text-[#53545a]">{q.questionnaireName}</p>
          <p className="text-xs text-[#919191]">
            {q.questionnaireDesc?.substring(0, 50)}{q.questionnaireDesc?.length > 50 ? '...' : ''}
          </p>
        </div>
      )
    },
    {
      key: 'library',
      header: 'Library',
      sortable: true,
      filterable: true,
      render: (q) => <span className="text-sm text-[#919191]">{q.library || '-'}</span>
    },
    {
      key: 'standards',
      header: 'Standards',
      sortable: true,
      filterable: true,
      render: (q) => <span className="text-sm text-[#919191]">{q.standards || '-'}</span>
    },
    {
      key: 'author',
      header: 'Author',
      sortable: true,
      filterable: true,
      render: (q) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-[#0077b5] text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {q.author?.charAt(0) || '?'}
          </div>
          <span className="text-sm text-[#53545a]">{q.author || '-'}</span>
        </div>
      )
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      accessor: (q) => getStatusLabel(q.status),
      render: (q) => (
        <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(q.status)}`}>
          {getStatusLabel(q.status)}
        </span>
      )
    },
    {
      key: 'questionCount',
      header: 'Questions',
      headerClassName: 'text-center',
      render: (q) => (
        <div className="flex justify-center">
          <button
            onClick={() => handleViewQuestions(q)}
            className="relative w-10 h-10 rounded-full bg-[#F47F2E]/10 hover:bg-[#F47F2E] text-[#F47F2E] hover:text-white flex items-center justify-center transition-all duration-200"
            title="View questions"
          >
            <FiList className="w-5 h-5" />
            <span className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] flex items-center justify-center px-1 rounded-full bg-[#F47F2E] text-white text-[10px] font-bold leading-none ring-2 ring-white">
              {q.questionCount || 0}
            </span>
          </button>
        </div>
      )
    }
  ];

  const tableActions = (q) => [
    { label: 'View Questions', icon: FiList, onClick: () => handleViewQuestions(q) },
    { label: 'Import', icon: FiUpload, onClick: () => openImportModal(q) },
    { label: 'Edit', icon: FiEdit2, onClick: () => openEditModal(q) },
    { divider: true },
    { label: 'Delete', icon: FiTrash2, onClick: () => openDeleteModal(q), danger: true }
  ];

  return (
    <div className="space-y-4">
      {/* Assessment Context Banner (when navigated from Assessment) */}
      {assessmentId && (
        <div className="bg-[#0077b5]/10 border border-[#0077b5]/30 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/assessments')}
              className="p-2 bg-white border border-[#0077b5]/30 text-[#0077b5] hover:bg-[#0077b5]/10 transition-colors"
              title="Back to Assessments"
            >
              <FiArrowLeft className="text-lg" />
            </button>
            <div>
              <p className="text-xs text-[#0077b5] font-medium uppercase tracking-wide">Questionnaires for Assessment</p>
              <p className="text-lg font-semibold text-[#53545a]">
                {assessmentContext?.assessmentDescription || assessmentContext?.accountName || `Assessment #${assessmentId}`}
              </p>
              {assessmentContext && (
                <p className="text-sm text-[#919191]">
                  {assessmentContext.accountName && <span>{assessmentContext.accountName}</span>}
                  {assessmentContext.vendorName && <span> &bull; {assessmentContext.vendorName}</span>}
                </p>
              )}
            </div>
          </div>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#0077b5]/20 text-[#0077b5]">
            ID: {assessmentId}
          </span>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className={`text-2xl font-bold text-[#53545a]`}>Add or edit a questionnaire</h1>
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#F47F2E] text-white">
            {questionnaires.length} questionnaires
          </span>
        </div>
        <nav className="flex items-center gap-1.5 text-sm">
          <button onClick={() => navigate('/assessments')} className="text-[#0077b5] hover:underline font-medium">Assessments</button>
          <span className="text-[#919191]">&gt;</span>
          <span className="text-[#53545a] font-medium">Questionnaires</span>
        </nav>
      </div>

      {/* Page subtitle */}
      {!loading && (
        <p className="text-sm text-[#919191]">
          {assessmentId && assessmentContext
            ? <>Questionnaires for <span className="font-semibold text-[#53545a]">{assessmentContext.assessmentDescription || assessmentContext.accountName || `Assessment #${assessmentId}`}</span>. Add or import into an existing one — select checkbox, then Import. Click the questions icon to view.</>
            : 'Add questionnaires or import questions into an existing one — select checkbox, then Import. Click the questions icon to view all questions.'}
        </p>
      )}

      {
        loading && (
           <div className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="w-80 h-9 bg-[#e6eef5] rounded animate-pulse mb-2" />
              
            </div>
            <div className="flex items-center justify-end gap-2">
              <div className="w-96 h-10 bg-[#e6eef5] rounded animate-pulse" />
           
            </div>
          </div>

         
        </div>
        )
      }

      {/* Data Table */}
      <DataTable
        columns={tableColumns}
        data={questionnaires}
        rowKey="questionnaireId"
        loading={loading}
        error={error}
        onRetry={fetchQuestionnaires}
        searchable={true}
        searchPlaceholder="Search questionnaires"
        actions={tableActions}
        selectable={true}
        selectedRows={selectedRowIds}
        onSelectionChange={setSelectedRowIds}
        headerActions={
          <>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f] transition-colors shadow-sm"
            >
              <FiPlus className="text-base" /> Add Questionnaire
            </button>
            <button
              onClick={handleHeaderImport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0077b5] text-white text-sm font-medium hover:bg-[#005f8f] transition-colors shadow-sm"
            >
              <FiUpload className="text-base" /> Import
            </button>
          </>
        }
        emptyMessage="No questionnaires found"
        totalLabel="questionnaires"
      />

      {/* Create/Edit Questionnaire Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={isEditing ? `Edit Questionnaire (ID: ${selectedQuestionnaire?.questionnaireId})` : "Add Questionnaire"}
        size="xl"
        footer={
          <div className="flex justify-between w-full">
            <div>
              {currentStep > 0 && (
                <button
                  onClick={handlePrevious}
                  className={`inline-flex items-center gap-2 px-4 py-2 border text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5`}
                >
                  <FiChevronLeft /> Previous
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setIsModalOpen(false); setCurrentStep(0); }}
                className={`px-4 py-2 border text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5`}
              >
                Cancel
              </button>
              {currentStep < steps.length - 1 ? (
                <button
                  onClick={handleNext}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f] transition-colors"
                >
                  Next <FiChevronRight />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f] transition-colors"
                >
                  {isEditing ? 'Update Questionnaire' : 'Create Questionnaire'}
                </button>
              )}
            </div>
          </div>
        }
      >
        {/* Step Indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      index < currentStep 
                        ? 'bg-[#0077b5] text-white' 
                        : index === currentStep 
                          ? 'bg-[#F47F2E] text-white' 
                          : 'bg-[#919191]/30 text-[#919191]'
                    }`}>
                      {index < currentStep ? <FiCheck className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                    </div>
                    <div className="mt-2 text-center hidden sm:block">
                      <p className={`text-xs font-medium ${index === currentStep ? 'text-[#F47F2E]' : 'text-[#53545a]'}`}>{step.title}</p>
                      <p className={`text-xs text-[#919191]`}>{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`w-12 sm:w-20 h-0.5 mx-2 ${
                      index < currentStep 
                        ? 'bg-[#0077b5]' 
                        : 'bg-[#919191]/30'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className={`p-4  bg-[#919191]/10`}>
          {renderStepContent()}
        </div>
      </Modal>

      {/* Import Questions from PDF Modal (two-step: upload PDF → select questions → save) */}
      <Modal
        isOpen={isImportModalOpen}
        onClose={() => { setIsImportModalOpen(false); setImportSaveResult(null); setImportStep('upload'); }}
        title={
          importStep === 'upload'
            ? `Import questions from a PDF${selectedQuestionnaire ? ` - ${selectedQuestionnaire.questionnaireName}` : ''}`
            : importStep === 'preview'
            ? 'Select the questions'
            : 'Import Results'
        }
        size="xl"
        footer={
          <div className="w-full flex items-center justify-between">
            {importStep === 'upload' && (
              <>
                <button
                  onClick={() => { setIsImportModalOpen(false); }}
                  className="px-5 py-2.5 border border-[#919191]/40 text-[#53545a]  text-sm font-medium hover:bg-[#919191]/5 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImportParse}
                  disabled={importLoading || !importFile}
                  className="px-8 py-2.5 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {importLoading ? (
                    <><FiLoader className="animate-spin" /> Extracting Questions...</>
                  ) : (
                    <><FiUpload /> Upload &amp; Parse</>
                  )}
                </button>
              </>
            )}
            {importStep === 'preview' && (
              <>
                <button
                  onClick={() => setImportStep('upload')}
                  className="px-5 py-2.5 border border-[#919191]/40 text-[#53545a]  text-sm font-medium hover:bg-[#919191]/5 transition-colors"
                >
                  &larr; Back
                </button>
                <button
                  onClick={handleImportSave}
                  disabled={importLoading || importSelectedRows.length === 0}
                  className="px-8 py-2.5 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {importLoading ? (
                    <><FiLoader className="animate-spin" /> Saving...</>
                  ) : (
                    <><FiCheck /> Save {importSelectedRows.length} Selected</>
                  )}
                </button>
              </>
            )}
            {importStep === 'result' && (
              <button
                onClick={() => { setIsImportModalOpen(false); setImportSaveResult(null); setImportStep('upload'); }}
                className="ml-auto px-5 py-2.5 bg-[#0077b5] text-white  text-sm font-medium hover:bg-[#006699] transition-colors"
              >
                Close
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-5">
          {/* Step 1: PDF File Upload */}
          {importStep === 'upload' && (
            <div>
              <label className="block text-sm font-medium text-[#53545a] mb-2">
                Choose a PDF file
              </label>
              <div
                onClick={() => importFileRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  importFile
                    ? 'border-[#0077b5] bg-[#0077b5]/10'
                    : 'border-[#919191]/50 hover:border-[#0077b5] hover:bg-[#919191]/5'
                }`}
              >
                <FiUpload className={`mx-auto text-3xl mb-3 ${importFile ? 'text-[#0077b5]' : 'text-[#919191]'}`} />
                {importFile ? (
                  <div>
                    <p className="text-sm font-medium text-[#53545a]">{importFile.name}</p>
                    <p className="text-xs text-[#919191] mt-1">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                    <p className="text-xs text-[#0077b5] mt-2">Click to change file</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm font-medium text-[#53545a]">
                      Click to upload a PDF file
                    </p>
                    <p className="text-xs text-[#919191] mt-1">
                      Upload a PDF containing questions and answer options
                    </p>
                  </div>
                )}
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleImportFileChange}
                  className="hidden"
                />
              </div>
            </div>
          )}

          {/* Step 2: Select Questions */}
          {importStep === 'preview' && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={importSelectedRows.length === importParsedRows.length && importParsedRows.length > 0}
                    onChange={toggleImportSelectAll}
                    className="w-4 h-4 rounded border-[#919191]/50 text-[#F47F2E] focus:ring-[#F47F2E]"
                  />
                  <span className="text-sm font-medium text-[#53545a]">Select the questions</span>
                </label>
                <span className="text-xs text-[#919191]">
                  {importSelectedRows.length} of {importParsedRows.length} selected
                </span>
              </div>

              <div className="max-h-[500px] overflow-y-auto space-y-2">
                {importParsedRows.map((q, idx) => {
                  const isSelected = importSelectedRows.includes(idx);
                  return (
                    <div
                      key={idx}
                      onClick={() => toggleImportRow(idx)}
                      className={`flex items-start gap-3 p-3  border cursor-pointer transition-colors ${
                        isSelected
                          ? 'border-[#F47F2E]/50 bg-[#F47F2E]/5'
                          : 'border-[#919191]/20 hover:bg-[#919191]/5'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleImportRow(idx)}
                        onClick={(e) => e.stopPropagation()}
                        className={`mt-0.5 w-4 h-4 rounded flex-shrink-0 ${
                          isSelected
                            ? 'text-[#F47F2E] border-[#F47F2E] focus:ring-[#F47F2E]'
                            : 'border-[#919191]/50 text-[#919191] focus:ring-[#919191]'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[#53545a] leading-relaxed">
                          {q.question}
                        </p>
                        {q.options && q.options.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {q.options.map((opt, optIdx) => (
                              <span
                                key={optIdx}
                                className="inline-flex px-2 py-0.5 rounded text-xs bg-[#919191]/10 text-[#919191]"
                              >
                                {opt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Import Results */}
          {importStep === 'result' && importSaveResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3  bg-[#919191]/10 border border-[#919191]/30 text-center">
                  <p className="text-2xl font-bold text-[#53545a]">{importSaveResult.totalQuestions}</p>
                  <p className="text-xs text-[#919191] mt-0.5">Total Questions</p>
                </div>
                <div className="p-3  bg-[#0077b5]/10 border border-[#0077b5]/30 text-center">
                  <p className="text-2xl font-bold text-[#0077b5]">{importSaveResult.successCount}</p>
                  <p className="text-xs text-[#0077b5] mt-0.5">Imported</p>
                </div>
                <div className={`p-3  text-center ${
                  importSaveResult.failedCount > 0
                    ? 'bg-[#F47F2E]/10 border border-[#F47F2E]/30'
                    : 'bg-[#919191]/10 border border-[#919191]/30'
                }`}>
                  <p className={`text-2xl font-bold ${
                    importSaveResult.failedCount > 0 ? 'text-[#F47F2E]' : 'text-[#919191]'
                  }`}>{importSaveResult.failedCount}</p>
                  <p className={`text-xs mt-0.5 ${
                    importSaveResult.failedCount > 0 ? 'text-[#F47F2E]' : 'text-[#919191]'
                  }`}>Failed</p>
                </div>
              </div>

              {importSaveResult.errors && importSaveResult.errors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#F47F2E] mb-2">
                    Errors ({importSaveResult.errors.length})
                  </h4>
                  <div className="max-h-48 overflow-y-auto border border-[#F47F2E]/30 ">
                    {importSaveResult.errors.map((err, idx) => (
                      <div key={idx} className="flex gap-3 px-3 py-2 text-xs border-b border-[#F47F2E]/20 last:border-b-0">
                        <span className="font-medium text-[#F47F2E] whitespace-nowrap">
                          Q{err.rowNumber}:
                        </span>
                        <span className="text-[#53545a]">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importSaveResult.failedCount === 0 && importSaveResult.successCount > 0 && (
                <div className="flex items-center gap-2 p-3  bg-[#0077b5]/10 border border-[#0077b5]/30">
                  <FiCheck className="text-[#0077b5] text-lg flex-shrink-0" />
                  <p className="text-sm text-[#0077b5]">
                    All {importSaveResult.successCount} question(s) imported successfully!
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Questionnaire"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setIsDeleteModalOpen(false)}
              className={`px-4 py-2 border  text-sm transition-colors border-[#919191]/40 text-[#53545a] hover:bg-[#919191]/5`}
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              className="px-4 py-2 bg-[#F47F2E] text-white  text-sm font-medium hover:bg-[#e06a1f]"
            >
              Delete
            </button>
          </>
        }
      >
        <div className="text-center py-4">
          <div className="w-16 h-16 bg-[#F47F2E]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiTrash2 className="text-[#F47F2E] text-2xl" />
          </div>
          <h3 className={`text-lg font-medium mb-2 text-[#53545a]`}>Are you sure?</h3>
          <p className="text-[#919191]">
            You are about to delete <span className={`font-medium text-[#53545a]`}>"{selectedQuestionnaire?.questionnaireName}"</span>.
            <br />This will also delete all associated questions.
          </p>
        </div>
      </Modal>


      {/* Toast Notification */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 px-5 py-3  shadow-lg text-white text-sm font-medium transition-all ${
          toast.type === 'success' ? 'bg-[#0077b5]' : 'bg-[#53545a]'
        }`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default Questionnaires;
